/**
 * When the end of a reading is a menu, not a conversation.
 *
 * A delivered reading ends with a short list of choices — Rate, Share, Finish,
 * sometimes more. Those choices were living inside the chat window, so the
 * bottom pane stayed sized for a conversation: composer, message area, and a
 * large empty region above two pills. On a 379px screen that cost roughly a
 * quarter of the height and cut the reading off mid-sentence.
 *
 * The options don't need a conversation pane. They need exactly their own
 * height. This module decides when to make that swap, and how tall the result
 * should be.
 */

/** A bubble with no words is chrome, not a conversation. */
function hasRealMessage(messages) {
  return (messages || []).some(m => String((m && m.message) || '').trim())
}

/**
 * Should the options take the pane instead of the chat?
 *
 * Conservative in one direction: keeping the chat when a menu would do costs
 * space, but taking the chat away from a seeker who still has something to say
 * is a dead end. So every uncertain case answers false.
 */
export function optionsOnly({
  readingOnScreen = false,
  readingReady = false,
  fetching = false,
  actions = null,
  messages = null,
  // Talk it through: a conversation exists for this reading (store.talk).
  talking = false,
} = {}) {
  if (!readingOnScreen || !readingReady) return false
  // The conversation after the reading needs its history and its reply box.
  if (talking) return false
  // Mid-stream the pane still belongs to the chat — the answer is arriving.
  if (fetching) return false
  if (!Array.isArray(actions) || actions.length === 0) return false
  // A follow-up exchange needs its history and its composer back.
  if (hasRealMessage(messages)) return false
  return true
}

// One option row + its gap, and the menu's own padding. Kept here rather than
// read back from the DOM so the pane can be sized before the menu paints —
// the measured fit then corrects it once it has.
const ROW_H = 44
const ROW_GAP = 8
const FRAME = 28

/**
 * The height a menu of `count` options wants, in px.
 *
 * Mirrors the component's own layout: three or more options go two-up, so the
 * rows are half the options rather than all of them. Capped at 34% of the
 * viewport — a long list scrolls inside the menu rather than swallowing the
 * reading it belongs to.
 *
 * This is the estimate used before the menu paints; the component then reports
 * its measured height and the split is corrected to that.
 */
export function menuHeightFor(count, { viewportH = 800 } = {}) {
  const n = Math.max(1, Number(count) || 0)
  const rows = n >= 3 ? Math.ceil(n / 2) : n
  const wanted = FRAME + rows * ROW_H + (rows - 1) * ROW_GAP
  return Math.min(wanted, Math.round(viewportH * 0.34))
}
